/**
 * AppIcon Module
 * Handles random app icon generation and management
 */
import type { Modal } from '../../ui/modal';

export class AppIcon {
    totalIcons: number;
    modal: Modal | null;
    currentIconPath: string | null;

    constructor(modal: Modal | null = null) {
        this.totalIcons = 14;
        this.modal = modal;
        this.currentIconPath = null;
        
        this.setRandomIcon();
    }

    /**
     * Generate and set a random app icon
     */
    setRandomIcon(): void {
        const randomIcon = Math.floor(Math.random() * this.totalIcons) + 1;
        // Use assets path for public directory files (Vite serves from public/)
        this.currentIconPath = `/assets/image-generator/logo_${randomIcon}.jpg`;
        
        this.updateFavicon();
        this.updateHeaderLogo();
    }

    /**
     * Update the favicon in the document head
     */
    updateFavicon(): void {
        if (!this.currentIconPath) return;
        
        const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement || document.createElement('link');
        link.type = 'image/png';
        link.rel = 'icon';
        link.href = this.currentIconPath;
        document.getElementsByTagName('head')[0].appendChild(link);
    }

    /**
     * Update the header logo and set up click handler
     */
    updateHeaderLogo(): void {
        if (!this.currentIconPath) return;
        
        const headerLogo = document.getElementById('headerLogo') as HTMLImageElement;
        if (headerLogo) {
            headerLogo.src = this.currentIconPath;
            
            // Handle image loading error by trying different paths
            headerLogo.onerror = () => {
                // Try with absolute path
                if (this.currentIconPath && !this.currentIconPath.startsWith('/')) {
                    const absolutePath = '/' + this.currentIconPath;
                    headerLogo.src = absolutePath;
                }
            };
            
            // Remove existing click listeners
            const newHeaderLogo = headerLogo.cloneNode(true) as HTMLImageElement;
            headerLogo.replaceWith(newHeaderLogo);
            const updatedLogo = document.getElementById('headerLogo') as HTMLImageElement;
            
            // Add click handler to show modal if available
            if (this.modal && updatedLogo) {
                updatedLogo.addEventListener('click', () => {
                    // Track the app icon click
                    if (window.analytics) {
                        window.analytics.trackEvent('app_icon_clicked', {
                            icon_number: this.getCurrentIconNumber(),
                            icon_path: this.currentIconPath
                        });
                    }
                    if (this.modal && this.currentIconPath) {
                        this.modal.showIcon(this.currentIconPath);
                    }
                });
            }
        }
    }

    /**
     * Set a specific icon by number
     * @param iconNumber - Icon number (1-14)
     */
    setSpecificIcon(iconNumber: number): void {
        if (iconNumber < 1 || iconNumber > this.totalIcons) {
            return;
        }
        
        // Use root path for public directory files (Vite serves from root)
        this.currentIconPath = `/logo_${iconNumber}.jpg`;
        this.updateFavicon();
        this.updateHeaderLogo();
    }

    /**
     * Get the current icon path
     * @returns Current icon path
     */
    getCurrentIconPath(): string | null {
        return this.currentIconPath;
    }

    /**
     * Get the current icon number
     * @returns Current icon number
     */
    getCurrentIconNumber(): number | null {
        if (!this.currentIconPath) return null;
        const match = this.currentIconPath.match(/logo_(\d+)\.jpg/);
        return match ? parseInt(match[1], 10) : null;
    }

    /**
     * Preload all app icons for better performance
     */
    preloadIcons(): void {
        for (let i = 1; i <= this.totalIcons; i++) {
            const img = new Image();
            img.src = `assets/image-generator/logo_${i}.jpg`;
        }
    }
}
